import PropTypes from 'prop-types';
import { useListContext, useTranslate } from 'react-admin';
import { useBarIndicatorsColors } from '@/composables/useBarIndicatorsColors';
import { Box } from '@mui/material';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import { BREAKPOINTS } from '@/constants/IndicatorBarBreakpoints';
import { DEFAULT_PERIOD } from '@/components/analytics/PeriodFilters.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';

const DeviceUptimeField = ({ record, className, defaultPeriod = DEFAULT_PERIOD }) => {
    const translate = useTranslate();
    const { filterValues } = useListContext();
    const currentPeriod = filterValues?.period ?? defaultPeriod;
    const analytics = record?.analytics?.[currentPeriod];
    const uptimeNumber = formatNumber((analytics?.uptime ?? 0) * 100, 0);
    const barColors = useBarIndicatorsColors(uptimeNumber, BREAKPOINTS);

    if (record.analytics === undefined) return null;

    return (
        <Box
            className={className}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                    gap: '8px',
                },
            }}
        >
            <Box sx={{ display: 'flex', gap: '2px' }}>
                {barColors.map((color, i) => (
                    <Box
                        key={i}
                        sx={{
                            width: '3px',
                            height: '16px',
                            borderRadius: '2px',
                            backgroundColor: color,
                        }}
                    />
                ))}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                }}
            >
                <Box
                    component='span'
                    sx={{
                        display: 'none',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        letterSpacing: '-0.11px',
                        color: Palette.black,
                        position: 'relative',
                        top: '2px',
                        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                            display: 'block',
                        },
                    }}
                >
                    {translate('common.uptime')}:
                </Box>
                <Box
                    component='span'
                    sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                            fontSize: '11px',
                        },
                    }}
                >
                    {uptimeNumber}%
                </Box>
            </Box>
        </Box>
    );
};

DeviceUptimeField.propTypes = {
    record: PropTypes.shape({
        analytics: PropTypes.shape({
            uptime: PropTypes.number,
        }),
    }),
    className: PropTypes.string,
};

export default DeviceUptimeField;
